import type { LocalDate } from "@js-joda/core";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import type { StaffRegisterStatus } from "services/api/company/timeReports.service";
import { useScheduleDayShifts } from "web-apps/company/components/schedule/components/hooks/useScheduleDayShifts.hook";
import { useWorkerAvatar } from "web-apps/company/components/schedule/components/hooks/useWorkerAvatar";
import { formatTimeRange } from "web-apps/company/components/schedule/utils/schedule.dates";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  formatLocalDateWeekdayLabel,
  getStockholmLocalDateNow,
} from "web-apps/company/utils/timeAndDate";
import {
  groupScheduleDayShifts,
  type DayShift,
} from "./scheduleDayShiftsPanel.logic";
import {
  findDayShiftClockStatus,
  toDayShiftStatusPresentation,
} from "./scheduleDayShiftsPanel.status.mapper";
import {
  DayShiftsContent,
  EmptyState,
  PartialLoadError,
  PublicShiftAvatar,
  PublicShiftName,
  ShiftAvatar,
  ShiftAvatarImage,
  ShiftCard,
  ShiftDetails,
  ShiftName,
  ShiftRole,
  ShiftStatus,
  ShiftStatusIcon,
  ShiftTime,
  TeamFilter,
  TeamFilters,
  TeamHeading,
  TeamSection,
  TeamShiftCount,
} from "./scheduleDayShiftsPanel.styled";

interface Props {
  readonly date: LocalDate | null;
  readonly onClose: () => void;
}

interface DayShiftCardProps {
  readonly clockStatusByTimeReportId: ReadonlyMap<number, StaffRegisterStatus>;
  readonly isToday: boolean;
  readonly shift: DayShift;
}

const DayShiftCard = ({
  clockStatusByTimeReportId,
  isToday,
  shift,
}: DayShiftCardProps): JSX.Element => {
  const { i18n, t } = useTranslation();
  const { isUnassigned } = shift;
  const avatarUrl = useWorkerAvatar({
    workerExternalId: shift.workerExternalId,
    isWorker: !isUnassigned,
  });
  const name = isUnassigned
    ? t("Schedule.Unassigned")
    : shift.workerName?.trim() || "-";
  const clockStatus = findDayShiftClockStatus({
    shift,
    statuses: clockStatusByTimeReportId,
  });
  const status = toDayShiftStatusPresentation({
    clockStatus,
    isToday,
    shift,
    translate: t,
  });

  return (
    <ShiftCard>
      <ShiftTime>
        {formatTimeRange(shift.startTime, shift.endTime, i18n.language)}
      </ShiftTime>
      {isUnassigned ? (
        <PublicShiftAvatar>
          <CustomIcon
            name="v2/warning-triangle"
            size="18px"
            color={ColorV2.NegativeRed}
          />
        </PublicShiftAvatar>
      ) : (
        <ShiftAvatar>
          {avatarUrl ? (
            <ShiftAvatarImage src={avatarUrl} alt="" />
          ) : (
            <CustomIcon name="v2/person" size="18px" color={ColorV2.Blue3} />
          )}
        </ShiftAvatar>
      )}
      <ShiftDetails>
        {isUnassigned ? (
          <PublicShiftName>{name}</PublicShiftName>
        ) : (
          <ShiftName>{name}</ShiftName>
        )}
        {shift.role && <ShiftRole>{shift.role}</ShiftRole>}
      </ShiftDetails>
      {status && (
        <ShiftStatus
          $backgroundColor={status.backgroundColor}
          $textColor={status.textColor}
        >
          <ShiftStatusIcon>
            <CustomIcon
              name={status.iconName}
              size="16px"
              color={status.iconColor}
            />
          </ShiftStatusIcon>
          {status.text}
        </ShiftStatus>
      )}
    </ShiftCard>
  );
};

export const ScheduleDayShiftsPanel = (props: Props): JSX.Element | null => {
  const [companyState] = useCompanyStore();
  const { i18n, t } = useTranslation();
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null);
  const { clockStatusByTimeReportId, state: dataState } = useScheduleDayShifts({
    companyId: companyState.company?.id,
    date: props.date,
  });

  useEffect(() => {
    setSelectedTeamId(null);
  }, [props.date]);

  const teams = useMemo(
    () =>
      dataState.status === "ready"
        ? groupScheduleDayShifts({ rows: dataState.data.items })
        : [],
    [dataState],
  );
  const visibleTeams = useMemo(
    () =>
      selectedTeamId === null
        ? teams
        : teams.filter((team) => team.id === selectedTeamId),
    [selectedTeamId, teams],
  );
  const title = props.date
    ? `${formatLocalDateWeekdayLabel(props.date, i18n.language, "short")} ${props.date.dayOfMonth()}/${props.date.monthValue()}`
    : "";
  const isToday = props.date?.equals(getStockholmLocalDateNow()) ?? false;
  return (
    <SidePanel
      isOpen={props.date !== null}
      onClose={props.onClose}
      title={title}
      titleId="schedule-day-shifts-title"
    >
      <DayShiftsContent aria-busy={dataState.status === "loading"}>
        {dataState.status === "ready" && teams.length > 1 && (
          <TeamFilters aria-label={t("Schedule.FilterDayShifts")}>
            <TeamFilter
              type="button"
              $isSelected={selectedTeamId === null}
              aria-pressed={selectedTeamId === null}
              onClick={() => setSelectedTeamId(null)}
            >
              {t("Schedule.All")}
            </TeamFilter>
            {teams.map((team) => (
              <TeamFilter
                key={team.id}
                type="button"
                $isSelected={selectedTeamId === team.id}
                aria-pressed={selectedTeamId === team.id}
                onClick={() => setSelectedTeamId(team.id)}
              >
                {team.name}
              </TeamFilter>
            ))}
          </TeamFilters>
        )}
        {dataState.status === "loading" ? (
          <EmptyState>{t("Schedule.LoadingDayShifts")}</EmptyState>
        ) : dataState.status === "error" ? (
          <EmptyState>{t("Schedule.LoadDayShiftsError")}</EmptyState>
        ) : visibleTeams.length === 0 ? (
          <EmptyState>{t("Schedule.NoDayShifts")}</EmptyState>
        ) : (
          visibleTeams.map((team) => (
            <TeamSection key={team.id}>
              <TeamHeading>
                {team.name}{" "}
                <TeamShiftCount>
                  {t("Schedule.ShiftCount", { count: team.shifts.length })}
                </TeamShiftCount>
              </TeamHeading>
              {team.shifts.map((shift) => (
                <DayShiftCard
                  key={shift.key}
                  clockStatusByTimeReportId={clockStatusByTimeReportId}
                  isToday={isToday}
                  shift={shift}
                />
              ))}
            </TeamSection>
          ))
        )}
        {dataState.status === "ready" && dataState.hasPartialLoadError && (
          <PartialLoadError role="status">
            {t("Schedule.LoadMoreDayShiftsError")}
          </PartialLoadError>
        )}
      </DayShiftsContent>
    </SidePanel>
  );
};
